<script setup lang="ts">
import { cva, VariantProps } from 'class-variance-authority'
import { computed, inject } from 'vue'
import { PhCaretRight, PhArrowRight } from '@phosphor-icons/vue'

import { Button, useDataLayer } from '@toni/brand-ui'
import { PurchaseOptions } from '@toni/commerce-api-types'

import PurchaseOptionLogo from './PurchaseOptionLogo.vue'
import PurchasePrice from './PurchasePrice.vue'
import PurchaseOptionMobile from './PurchaseOptionMobile.vue'

const purchaseOptionVariants = cva(
  'rounded-lg border border-neutral-200 flex justify-between font-heading text-heading-sm items-center w-full pl-3 pr-4 min-h-[3.375rem]',
  {
    variants: {
      variant: {
        primary: 'text-neutral-950',
        highlighted: 'bg-primary-500 text-white'
      },
      size: {
        md: 'py-0.5',
        lg: 'py-2 lg:pl-6'
      }
    }
  }
)

export type PurchaseOptionVariants = typeof purchaseOptionVariants
export type PurchaseOptionProps = VariantProps<typeof purchaseOptionVariants>

const props = withDefaults(
  defineProps<{
  variant?: PurchaseOptionProps['variant']
  size?: PurchaseOptionProps['size']
  purchaseOption: PurchaseOptions[0]
  purchaseOptionPosition?: number,
  isComparison?: boolean,
  type?: 'regular' | 'icon-only'
}>(),
  {
    variant: 'primary',
    size: 'lg',
    purchaseOptionPosition: 0,
    isComparison: false,
    type: 'regular'
  }
)

const purchaseOption = computed(() => props.purchaseOption)

const variant = computed(() => props.variant)
const {
  merchant,
  info: { detailPageUrl }
} = purchaseOption.value

const validMerchantData = computed((): boolean => {
  return !!(merchant.name && detailPageUrl)
})

const productCardTracking = props.purchaseOptionPosition
  ? inject('productCardTracking') as {
      product_card_id: string
      product_card_position: string
      product_card_rel_position: string
    }
  : null

const handlePurchaseOptionClick = () => {
  if (productCardTracking && productCardTracking.product_card_id && productCardTracking.product_card_position && productCardTracking.product_card_rel_position) {
    const dataLayer = useDataLayer()

    const purchaseOptionTrackingData = {
      event: 'product_card_click',
      product_card_id: productCardTracking.product_card_id,
      placement: 'product_card',
      merchant: merchant.name,
      affiliate_link_position: props.purchaseOptionPosition,
      product_card_position: productCardTracking.product_card_position,
      product_card_rel_position: productCardTracking.product_card_rel_position
    }

    dataLayer.push(purchaseOptionTrackingData)
  }
}
</script>

<template>
  <PurchaseOptionMobile v-if="validMerchantData"
                        class="lg:hidden"
                        :variant="variant"
                        :size="size"
                        :purchase-option="purchaseOption"
                        :is-comparison="isComparison"
                        :purchase-option-variants="purchaseOptionVariants"
                        :handle-purchase-option-click="handlePurchaseOptionClick" />
  <div
    v-if="validMerchantData"
    class="hidden lg:flex w-full flex-col justify-center items-center gap-2 ">
    <PurchaseOptionLogo v-if="isComparison"
                        :merchant="merchant"
                        variant="primary"
                        size="lg" />
    <div :class="purchaseOptionVariants({ variant, size })">
      <PurchaseOptionLogo v-if="!isComparison"
                          :merchant="merchant"
                          :variant="variant"
                          :size="size" />

      <div class="flex gap-1"
           :class="isComparison ? 'justify-between w-full' : ''">
        <PurchasePrice :purchase-option="purchaseOption"
                       :size="size"
                       :is-comparison="isComparison" />
        <Button variant="primary"
                :inverted="variant !== 'highlighted'"
                class="purchase-option-action flex items-center hover:border-secondary-500"
                :href="detailPageUrl!"
                target="_blank"
                rel="noopener noreferrer sponsored"
                :type="type"
                @click="handlePurchaseOptionClick">
          <div v-if="size !== 'lg'"
               class="flex items-center">
            <PhCaretRight size="20"
                          class="mx-2" />
          </div>
          <div v-if="size === 'lg'"
               class="flex items-center">
            <span class="text-button-xs">{{ $t('commerce.viewOffer') }}</span>
            <PhArrowRight size="18" />
          </div>
        </Button>
      </div>
    </div>
  </div>
</template>
